import type { Band } from '@siteday/shared';

import { bandGlyph, bandLabel } from '@/lib/bands';
import { cn } from '@/lib/cn';

const toneClass: Record<Band, string> = {
  green: 'bg-good-bg text-good',
  amber: 'bg-warn-bg text-warn',
  red: 'bg-bad-bg text-bad',
  none: 'bg-bg2 text-ink3',
};

export interface BandPillProps {
  band: Band;
  pct: number | null;
  /** Text for band "none" (e.g. "no entry", "not started"). */
  fallback?: string | undefined;
  /** Replace the "▲ 90%" text (legend use). */
  label?: string | undefined;
  className?: string | undefined;
}

/** Icon + percent so the band reads without color (doc 08 W2). */
export function BandPill({
  band,
  pct,
  fallback = 'no entry',
  label: override,
  className,
}: BandPillProps) {
  const label = override ?? bandLabel(band, pct, fallback);
  const name = band === 'none' ? fallback : `${band} band, ${pct}% of plan`;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-[10px] px-1.5 py-px font-cond text-[11px] font-bold tracking-wide num',
        toneClass[band],
        className,
      )}
      aria-label={name}
      title={name}
    >
      {override ? `${bandGlyph(band)} ${override}`.trim() : label}
    </span>
  );
}

export interface QuantityLineProps {
  code: string;
  actual: string | null;
  plan: string | null;
  unit: string;
  band: Band;
  pct: number | null;
  format: (v: string) => string;
}

/** "02-300  180 / 200 LF  ▲ 90%" — the dashboard quantity line. */
export function QuantityLine({ code, actual, plan, unit, band, pct, format }: QuantityLineProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap font-semibold num',
        band === 'none' && 'font-medium text-ink3',
      )}
    >
      <code className="w-12 text-xs font-bold text-ink3">{code}</code>
      <span>
        {actual === null ? '—' : format(actual)} / {plan === null ? '—' : format(plan)} {unit}
      </span>
      <BandPill band={band} pct={pct} />
    </span>
  );
}
